import { useEffect, useState } from 'react';
import { supabase } from '../../config/supabaseClient';
import CartLines from './CartLines';
import {
  PartsOrderApiError,
  PartsOrderCartItem,
  WidgetAccount,
  cartTotal,
  copyFor,
  formatCurrency,
  submitOrder,
} from '../../services/partsOrderApi';
import type { OrderConfirmation } from './PartsOrderApp';

interface PickupLocation {
  id: number;
  name: string;
  city: string;
  state: string;
}

interface ReviewStepProps {
  organizationId: string;
  codeId: number | null;
  session: string | null;
  leadMode: boolean;
  enrichment: { email?: string; phone?: string };
  cart: PartsOrderCartItem[];
  /** Accounts on the verified phone; the picker shows only when there are several. */
  accounts?: WidgetAccount[];
  onUpdateQuantity: (partNumber: string, quantity: number) => void;
  onRemove: (partNumber: string) => void;
  onBack: () => void;
  onSubmitted: (confirmation: OrderConfirmation) => void;
  onVerificationExpired: () => void;
  /** When set, an already_submitted code bounces here instead of reading as a fresh success. */
  onAlreadySubmitted?: () => void;
}

function ReviewStep({
  organizationId,
  codeId,
  session,
  leadMode,
  enrichment,
  cart,
  accounts = [],
  onUpdateQuantity,
  onRemove,
  onBack,
  onSubmitted,
  onVerificationExpired,
  onAlreadySubmitted,
}: ReviewStepProps) {
  const [locations, setLocations] = useState<PickupLocation[] | null>(null);
  const [locationId, setLocationId] = useState<number | ''>('');
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [name, setName] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [comments, setComments] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchLocations = async () => {
      const { data, error: fetchError } = await supabase
        .from('locations')
        .select('id, name, city, state')
        .eq('organization_id', parseInt(organizationId, 10))
        .order('name');
      if (cancelled) return;
      if (fetchError) {
        setError("We couldn't load pickup locations. Please try again.");
        setLocations([]);
        return;
      }
      const rows = (data ?? []) as PickupLocation[];
      setLocations(rows);
      if (rows.length === 1) setLocationId(rows[0].id);
    };
    void fetchLocations();
    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  const total = cartTotal(cart);
  const multiAccount = accounts.length > 1;
  const accountId = customerId ?? accounts[0]?.id; // default: first account

  const handleSubmit = async () => {
    if (codeId == null || locationId === '' || cart.length === 0 || submitting) return;
    setSubmitting(true);
    setError(null);
    const notes = [poNumber.trim() && `PO: ${poNumber.trim()}`, comments.trim()]
      .filter(Boolean)
      .join(' — ');
    try {
      const result = await submitOrder(
        organizationId,
        codeId,
        locationId,
        cart.map((i) => ({ part_number: i.part_number, quantity: i.quantity })),
        {
          ...(leadMode && name.trim() ? { name: name.trim() } : {}),
          ...enrichment,
          ...(multiAccount && accountId != null ? { customerId: accountId } : {}),
          ...(notes ? { notes } : {}),
        },
        session,
      );
      if (result.status === 'lead') {
        onSubmitted({ type: 'lead' });
      } else {
        onSubmitted({
          type: 'order',
          orderNumber: result.order_number,
          estimatedReadyAt: result.estimated_ready_at,
          locationName: result.location_name,
        });
      }
    } catch (err) {
      if (err instanceof PartsOrderApiError) {
        if (err.code === 'verification_required') {
          onVerificationExpired();
          return;
        }
        // The code already produced an order — show it as a success, unless the
        // host keeps one code for many orders and wants a re-verify instead.
        if (err.code === 'already_submitted') {
          if (onAlreadySubmitted) onAlreadySubmitted();
          else onSubmitted({ type: 'order', orderNumber: err.orderNumber });
          return;
        }
        setError(err.message);
      } else {
        setError(copyFor(undefined));
      }
      setSubmitting(false);
    }
  };

  return (
    <div className="form-step">
      <h2>Review Your Order</h2>
      <p className="step-intro">
        Choose a pickup location to see availability there, check quantities, and submit your order.
      </p>

      {multiAccount && (
        <div className="form-group">
          <label htmlFor="orderAccount">This order is for</label>
          <select
            id="orderAccount"
            value={accountId}
            onChange={(e) => setCustomerId(Number(e.target.value))}
            disabled={submitting}
          >
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>{a.display_name}</option>
            ))}
          </select>
        </div>
      )}

      <div className="form-group">
        <label htmlFor="pickupLocation" className="required">Pickup Location</label>
        {locations == null ? (
          <p className="parts-order-empty">Loading locations…</p>
        ) : (
          <select
            id="pickupLocation"
            value={locationId}
            onChange={(e) => setLocationId(e.target.value ? Number(e.target.value) : '')}
            disabled={submitting}
          >
            <option value="">Select a location…</option>
            {locations.map((loc) => (
              <option key={loc.id} value={loc.id}>
                {loc.name} — {loc.city}, {loc.state}
              </option>
            ))}
          </select>
        )}
      </div>

      {cart.length === 0 ? (
        <p className="parts-order-empty">Your cart is empty — head back to add some parts.</p>
      ) : (
        <div className="cart-panel cart-panel--expanded parts-order-review-cart">
          <div className="cart-panel__body">
            <CartLines
              items={cart}
              onUpdateQuantity={onUpdateQuantity}
              onRemove={onRemove}
              disabled={submitting}
              pickupLocationId={locationId === '' ? null : locationId}
            />
          </div>
          {multiAccount ? (
            // Prices were quoted for the first account; the total depends on the one picked.
            <div className="parts-order-review-total">
              <span className="cart-panel__label">Final price is set for the account you choose.</span>
            </div>
          ) : total != null && (
            <div className="parts-order-review-total">
              <span className="cart-panel__label">Total</span>
              <span className="cart-panel__total">{formatCurrency(total)}</span>
            </div>
          )}
        </div>
      )}

      {leadMode && (
        <p className="parts-order-lead-note">
          We couldn't match your contact info to an account, so our parts team will confirm
          availability and pricing with you before anything is ordered.
        </p>
      )}

      {leadMode && (
        <div className="form-group">
          <label htmlFor="orderName">Your Name</label>
          <input
            type="text"
            id="orderName"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="First and last name"
            disabled={submitting}
          />
        </div>
      )}

      <div className="form-group">
        <label htmlFor="orderPo">PO Number (optional)</label>
        <input
          type="text"
          id="orderPo"
          value={poNumber}
          onChange={(e) => setPoNumber(e.target.value)}
          disabled={submitting}
        />
      </div>

      <div className="form-group">
        <label htmlFor="orderComments">Comments (optional)</label>
        <textarea
          id="orderComments"
          rows={3}
          value={comments}
          onChange={(e) => setComments(e.target.value)}
          placeholder="Anything our parts team should know?"
          disabled={submitting}
        />
      </div>

      {error && <div className="error-text">{error}</div>}

      <div className="parts-order-review-actions">
        <button type="button" className="btn btn-secondary" onClick={onBack} disabled={submitting}>
          Keep shopping
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSubmit}
          disabled={submitting || cart.length === 0 || locationId === '' || codeId == null}
        >
          {submitting ? 'Submitting…' : 'Submit order'}
        </button>
      </div>
    </div>
  );
}

export default ReviewStep;
